import { useState } from 'react';
import { Copy, Check } from 'lucide-react';
import { cn } from '../../lib/cn';

interface CopyFieldProps {
  value: string;
  label?: string;
  onCopy?: () => void;
  className?: string;
}

export function CopyField({ value, label, onCopy, className }: CopyFieldProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      onCopy?.();
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      // clipboard unavailable; the toast in the caller will not fire
    }
  };

  return (
    <div className={cn('w-full', className)}>
      {label && <span className="mb-2 block text-sm font-medium text-text">{label}</span>}
      <div className="flex items-stretch gap-2">
        <input
          readOnly
          value={value}
          onFocus={(e) => e.currentTarget.select()}
          aria-label={label ?? 'Value'}
          className="min-w-0 flex-1 rounded-xl border border-border bg-surface-elevated px-4 py-3 font-mono text-sm text-text focus:outline-none focus:border-primary"
        />
        <button
          type="button"
          onClick={handleCopy}
          aria-label={copied ? 'Copied' : 'Copy'}
          className={cn(
            'shrink-0 inline-flex items-center justify-center px-3 rounded-xl border border-border bg-surface-elevated text-text-muted',
            'hover:text-primary hover:border-primary transition-colors active:scale-[0.98]',
          )}
        >
          {copied ? <Check size={18} className="text-accent" /> : <Copy size={18} />}
        </button>
      </div>
    </div>
  );
}
